import { Component, Input, OnInit, inject, input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { StructureType } from '../../types/fields';
import { OcrPrimeStore } from '../../store/ocr-prime.store';

@Component({
  selector: 'app-template-metadata-fields',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <div [formGroup]="metadataForm" class="space-y-5">
      <div class="space-y-1.5">
        <label for="name" class="block text-sm font-medium text-zinc-700 dark:text-zinc-300">
          Template Name <span class="text-red-500">*</span>
        </label>
        <input
          data-testid="template-metadata-name-input"
          type="text"
          id="name"
          formControlName="name"
          placeholder="e.g. Acme Corp Invoice"
          class="block w-full rounded-lg border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-700 px-3.5 py-2.5 text-sm text-zinc-900 dark:text-zinc-100 shadow-sm placeholder:text-zinc-400 dark:placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all hover:bg-zinc-50 dark:hover:bg-zinc-600"
        >
        @if (metadataForm.get('name')?.invalid && metadataForm.get('name')?.touched) {
          <p class="text-xs text-red-600">Please enter a template name</p>
        }
      </div>

      <div class="space-y-1.5">
        <label for="description" class="block text-sm font-medium text-zinc-700 dark:text-zinc-300">
          Description <span class="text-red-500">*</span>
        </label>
        <p class="text-xs text-zinc-500 dark:text-zinc-400">These instructions are specific to this template and will guide the AI during document processing.</p>
        <textarea
          data-testid="template-metadata-description-input"
          id="description"
          formControlName="description"
          rows="2"
          placeholder="What is this template for?"
          class="block w-full rounded-lg border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-700 px-3.5 py-2.5 text-sm text-zinc-900 dark:text-zinc-100 shadow-sm placeholder:text-zinc-400 dark:placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all hover:bg-zinc-50 dark:hover:bg-zinc-600 resize-y min-h-[56px]"
        ></textarea>
        @if (metadataForm.get('description')?.invalid && metadataForm.get('description')?.touched) {
          <p class="text-xs text-red-600">Please enter a template description</p>
        }
      </div>

      <div class="space-y-1.5">
        <label for="fieldsStructureId" class="block text-sm font-medium text-zinc-700 dark:text-zinc-300">
          Structure Schema <span class="text-red-500">*</span>
        </label>
        <div class="relative">
          <select
            data-testid="template-metadata-structure-select"
            id="fieldsStructureId"
            formControlName="fieldsStructureId"
            [disabled]="isExtracting()"
            class="block w-full rounded-lg border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-700 px-3.5 py-2.5 text-sm text-zinc-900 dark:text-zinc-100 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all hover:bg-zinc-50 dark:hover:bg-zinc-600 appearance-none pr-10 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <option value="" disabled>Select a field structure</option>
            @for (structure of structures; track structure.id) {
              <option [value]="structure.id">{{ structure.name }}</option>
            }
          </select>
          <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
            <svg class="h-4 w-4 text-zinc-400 dark:text-zinc-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
            </svg>
          </div>
        </div>
        <p class="text-xs text-zinc-500 dark:text-zinc-400">This defines which fields will be extracted.</p>
        @if (metadataForm.get('fieldsStructureId')?.invalid && metadataForm.get('fieldsStructureId')?.touched) {
          <p class="text-xs text-red-600">Please select a field structure</p>
        }
      </div>
    </div>
  `
})
export class TemplateMetadataFieldsComponent implements OnInit {
  @Input({ required: true }) metadataForm!: FormGroup;
  isExtracting = input(false);

  readonly store = inject(OcrPrimeStore);

  get structures(): StructureType[] {
    return this.store.structures();
  }

  ngOnInit(): void {
    this.store.loadStructures();
  }
}
